import { useEffect, useId, useRef, useState } from "react";
import { TbChevronLeft, TbChevronRight } from "react-icons/tb";
import { Icon } from "../Icon";
import { ActionButton } from "./Actions";
import "./styles/filters.scss";

// Controls of a list toolbar and its footer (lots, tanks...): search box, labelled select, pagination.
// They are controlled and know no wording: the caller passes every label.

// Search box with a magnifier. `label` is the accessible name (the placeholder alone is not one).
export const SearchField = ({ value, onChange, placeholder, label, className }) => (
  <div className={["filter_search", className].filter(Boolean).join(" ")}>
    <Icon icon="fa-search" size="md" aria-hidden="true" />
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      aria-label={label || placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

// Select with its label on the left. options: [{ value, label }]; the caller includes the "all" option (value "").
export const FilterSelect = ({ label, value, onChange, options, disabled, className }) => {
  const id = useId();
  return (
    <div className={["filter_select", className].filter(Boolean).join(" ")}>
      <label htmlFor={id}>{label}</label>
      <div className="filter_select_control">
        <select id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Icon icon="fa-angle-down" size="md" aria-hidden="true" />
      </div>
    </div>
  );
};

// Label + text/number/date input for the advanced filters panel; min/max bound it.
export const FilterInput = ({ label, value, onChange, type = "text", min, max, disabled, className }) => {
  const id = useId();
  return (
    <div className={["filter_input", className].filter(Boolean).join(" ")}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
};

// "Filters" button that opens a panel with the less used filters (children). Closes on Escape and on a click
// outside. `activeCount` is shown on the button; `onReset` adds a clear button inside the panel.
export const FilterPanel = ({ label, resetLabel, activeCount = 0, onReset, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !ref.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [isOpen]);

  return (
    <div className={["filter_panel", className].filter(Boolean).join(" ")} ref={ref}>
      <ActionButton
        icon="fa-sliders"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        {label}
        {activeCount > 0 && <span className="filter_panel_count">{activeCount}</span>}
        <Icon icon="fa-angle-down" size="sm" aria-hidden="true" />
      </ActionButton>
      {isOpen && (
        <div id={panelId} className="filter_panel_body" role="group" aria-label={label}>
          {children}
          {onReset && (
            <ActionButton onClick={onReset} disabled={activeCount === 0}>
              {resetLabel}
            </ActionButton>
          )}
        </div>
      )}
    </div>
  );
};

// Footer: rows per page, previous / pages / next, "1-10 of 24".
// pagination: { page, pageCount, from, to, total } (0-based page, as returned by paginate()).
// labels: { rowsPerPage, previous, next, page: (n) => accessible name of page n, range: ({from,to,total}) => text }.
export const PaginationBar = ({ pagination, pageSize, pageSizes = [10, 20, 30, 40, 50], onPageChange, onPageSizeChange, labels }) => {
  const id = useId();
  const { page, pageCount } = pagination;
  // First, last, the current and its neighbours; "..." is not a button, so it is skipped when a gap is single.
  const pages = [...new Set([0, page - 1, page, page + 1, pageCount - 1])].filter((p) => p >= 0 && p < pageCount).sort((a, b) => a - b);

  return (
    <nav className="filter_pagination" aria-label={labels.navigation}>
      {onPageSizeChange && (
        <div className="filter_pagination_size">
          <label htmlFor={id}>{labels.rowsPerPage}</label>
          <select id={id} value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="filter_pagination_pages">
        <button type="button" aria-label={labels.previous} disabled={page <= 0} onClick={() => onPageChange(page - 1)}>
          <Icon icon={TbChevronLeft} size="sm" aria-hidden="true" />
        </button>
        {pages.map((p, index) => (
          <span key={p} className="filter_pagination_item">
            {index > 0 && p - pages[index - 1] > 1 && <span aria-hidden="true">…</span>}
            <button
              type="button"
              aria-label={labels.page(p + 1)}
              aria-current={p === page ? "page" : undefined}
              className={p === page ? "active" : undefined}
              onClick={() => onPageChange(p)}
            >
              {p + 1}
            </button>
          </span>
        ))}
        <button type="button" aria-label={labels.next} disabled={page >= pageCount - 1} onClick={() => onPageChange(page + 1)}>
          <Icon icon={TbChevronRight} size="sm" aria-hidden="true" />
        </button>
      </div>
      <p className="filter_pagination_range">{labels.range(pagination)}</p>
    </nav>
  );
};
